import * as React from 'react';
import clsx from 'clsx';
import type { ToastData } from '@/context/ToastContext';
import { useToast } from '@/context/ToastContext';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNow } from 'date-fns';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationItem: React.FC<{
  notification: ToastData;
  onDelete: (id: string) => void;
}> = ({ notification, onDelete }) => {
  const getBgColor = () => {
    if (notification.read) return 'bg-gray-50';
    return 'bg-white';
  };

  const getTypeColor = () => {
    switch (notification.type) {
      case 'success':
        return 'border-l-accent-300';
      case 'error':
        return 'border-l-red-400';
      case 'info':
        return 'border-l-brand-300';
      default:
        return 'border-l-gray-300';
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true });

  return (
    <div
      className={clsx(
        'group relative border-b border-gray-200 px-4 py-3 transition-colors hover:bg-gray-100',
        'border-l-4',
        getBgColor(),
        getTypeColor()
      )}
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='flex-1'>
          <div className='mb-1 flex items-start justify-between'>
            <h4 className='font-sans text-14 font-semibold text-gray-800'>{notification.title}</h4>
            {!notification.read && (
              <span className='ml-2 size-2 shrink-0 rounded-full bg-brand-300' />
            )}
          </div>
          <p className='font-sans text-12 text-gray-600'>{notification.subtitle}</p>
          <p className='mt-1 font-sans text-10 text-gray-450'>{timeAgo}</p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          className='opacity-0 transition-opacity hover:opacity-100 group-hover:opacity-100'
          aria-label='Delete notification'
        >
          <XMarkIcon className='size-4 text-gray-450 hover:text-gray-600' />
        </button>
      </div>
    </div>
  );
};

/** Slide-down panel listing past toast history. Anchored under the bell button; closes on outside click. */
export const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const { notificationHistory, deleteNotification, markAsRead } = useToast();
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close panel when clicking outside
  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Mark all as read when opening panel
  React.useEffect(() => {
    if (isOpen && notificationHistory.length > 0) {
      notificationHistory.forEach((notification) => {
        if (!notification.read) {
          markAsRead(notification.id);
        }
      });
    }
  }, [isOpen, notificationHistory, markAsRead]);

  if (!isOpen) return null;

  const unreadCount = notificationHistory.filter((notif) => !notif.read).length;

  return (
    <div
      ref={panelRef}
      className={clsx(
        'fixed z-popover overflow-hidden rounded-12 bg-white shadow-lg',
        // Mobile: Full width with padding
        'inset-x-4 top-16 max-h-[calc(100vh-5rem)]',
        // Desktop: Fixed width, positioned near notification button
        'md:left-auto md:right-6 md:top-20 md:w-[420px]'
      )}
    >
      {/* Header */}
      <div className='border-b border-gray-200 bg-white px-4 py-3 shadow-sm'>
        <div className='flex items-center justify-between'>
          <h3 className='font-proxima text-18 font-bold text-gray-800'>Notifications</h3>
          <button
            onClick={onClose}
            className='rounded-full p-1 transition-colors hover:bg-gray-100'
            aria-label='Close notifications'
          >
            <XMarkIcon className='size-5 text-gray-600' />
          </button>
        </div>
        {unreadCount > 0 && (
          <p className='mt-1 font-sans text-12 text-brand-300'>
            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Notification List */}
      <div className='max-h-[calc(100vh-12rem)] overflow-y-auto md:max-h-[500px]'>
        {notificationHistory.length === 0 ? (
          <div className='flex flex-col items-center justify-center py-12'>
            <div className='mb-3 rounded-full bg-gray-100 p-4'>
              <svg
                className='size-8 text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9'
                />
              </svg>
            </div>
            <p className='font-sans text-14 font-medium text-gray-600'>No notifications</p>
            <p className='mt-1 font-sans text-12 text-gray-450'>You&apos;re all caught up!</p>
          </div>
        ) : (
          notificationHistory.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onDelete={deleteNotification}
            />
          ))
        )}
      </div>

      {/* Footer - Optional */}
      {notificationHistory.length > 0 && (
        <div className='border-t border-gray-200 bg-gray-50 px-4 py-2 text-center'>
          <p className='font-sans text-11 text-gray-500'>
            Showing notifications from the last 3 days
          </p>
        </div>
      )}
    </div>
  );
};
